import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API = 'https://cotizaciones-fabrica-rivera-production.up.railway.app/api';
const BASE = 'https://cotizaciones-fabrica-rivera-production.up.railway.app';
const UNIDADES = ['unidad', 'metro lineal', 'metro cuadrado'];
// Categorías principales, y las subcategorías de algunas de ellas aparte (el
// catálogo público de la tienda las agrupa en un submenú bajo su categoría).
// Se muestran agrupadas visualmente para que quede claro que hay que elegir
// la subcategoría específica del producto (no la principal y la sub a la vez).
const CATEGORIAS = ['Cocción', 'Hornos', 'Muebles', 'Hogar', 'Industrial', 'Arquitectura', 'Construcción', 'Hospitalario', 'Alimentos y Bebidas', 'Restaurante', 'Hotelería'];
const SUBCATEGORIAS = {
  'Cocción': ['Estufa industrial', 'Parrillas/Grill', 'Planchas', 'Freidores', 'Multiservicios', 'Baño Marías'],
  'Hornos': ['Hornos de repostería', 'Hornos de pizza', 'Hornos de panadería', 'Hornos de cámara'],
  'Muebles': ['Muebles de lavado', 'Muebles de trabajo', 'Muebles varios'],
};

const imgSrc = (url) => {
  if (!url) return null;
  return url.startsWith('http') ? url : `${BASE}${url}`;
};

export default function Productos() {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const [productos, setProductos] = useState([]);
  const [form, setForm] = useState({ codigo:'', nombre:'', descripcion:'', tipo:'producto', precio_base:'', unidad:'unidad', aplica_iva:true, stock:0, visible_tienda:true, categorias:[] });
  const [imagen, setImagen] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [busqueda, setBusqueda] = useState('');

  const productosFiltrados = productos.filter(p => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return true;
    return [p.nombre, p.codigo, p.descripcion, ...(p.categorias || [])]
      .some(campo => (campo || '').toLowerCase().includes(q));
  });

  // ── Fotos adicionales y video (solo se usan para llenar la galería de la tienda) ──
  const [imagenesNuevas, setImagenesNuevas] = useState([]);
  const [imagenesExistentes, setImagenesExistentes] = useState([]);
  const [eliminarImagenes, setEliminarImagenes] = useState([]);
  const [video, setVideo] = useState(null);
  const [videoExistente, setVideoExistente] = useState(null);
  const [eliminarVideo, setEliminarVideo] = useState(false);

  const descripcionRef = useRef(null);

  const cargar = () => axios.get(`${API}/productos`, { headers }).then(r => setProductos(r.data));
  useEffect(() => { cargar(); }, []);

  // Ajusta la altura del textarea de descripción cuando se abre el formulario
  // (crear o editar) para que un texto largo ya existente se vea completo
  // desde el principio, no solo después de escribir algo.
  useEffect(() => {
    if (descripcionRef.current) {
      descripcionRef.current.style.height = 'auto';
      descripcionRef.current.style.height = descripcionRef.current.scrollHeight + 'px';
    }
  }, [mostrarForm, editandoId]);

  const abrirNuevo = () => {
    setForm({ codigo:'', nombre:'', descripcion:'', tipo:'producto', precio_base:'', unidad:'unidad', aplica_iva:true, stock:0, visible_tienda:true, categorias:[] });
    setImagen(null); setPreview(null); setEditandoId(null); setMostrarForm(true);
    setImagenesNuevas([]); setImagenesExistentes([]); setEliminarImagenes([]);
    setVideo(null); setVideoExistente(null); setEliminarVideo(false);
  };

  const abrirEditar = (p) => {
    setForm({ codigo:p.codigo, nombre:p.nombre, descripcion:p.descripcion||'', tipo:p.tipo, precio_base:p.precio_base, unidad:p.unidad, aplica_iva:p.aplica_iva, stock:p.stock || 0, visible_tienda: p.visible_tienda !== false, categorias: p.categorias || [] });
    setImagen(null);
    setPreview(imgSrc(p.imagen_url));
    setEditandoId(p.id); setMostrarForm(true);
    setImagenesNuevas([]);
    setImagenesExistentes(p.imagenes_adicionales || []);
    setEliminarImagenes([]);
    setVideo(null);
    setVideoExistente(p.video_url || null);
    setEliminarVideo(false);
    // El formulario está arriba de la página y la lista de productos más abajo;
    // sin esto, al editar un producto lejos en la lista hay que scrollear a mano.
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelar = () => {
    setMostrarForm(false); setEditandoId(null); setImagen(null); setPreview(null);
    setImagenesNuevas([]); setImagenesExistentes([]); setEliminarImagenes([]);
    setVideo(null); setVideoExistente(null); setEliminarVideo(false);
  };

  const handleImagen = (e) => {
    const file = e.target.files[0];
    setImagen(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleImagenesNuevas = (e) => setImagenesNuevas(Array.from(e.target.files));
  const handleVideo = (e) => setVideo(e.target.files[0] || null);
  const toggleEliminarImagen = (url) => {
    setEliminarImagenes(prev => prev.includes(url) ? prev.filter(u => u !== url) : [...prev, url]);
  };

  // Un producto solo puede tener una categoría principal: al elegir una se
  // reemplaza la anterior (o se quita si vuelves a hacer clic en la ya
  // seleccionada), y se limpian las subcategorías de Cocción si había alguna.
  const seleccionarCategoria = (cat) => {
    setForm(prev => ({
      ...prev,
      categorias: prev.categorias.includes(cat) ? [] : [cat]
    }));
  };

  // Las subcategorías de Cocción se combinan entre sí (un producto puede ser,
  // por ejemplo, "Parrillas/Grill" y "Multiservicios" a la vez) y conviven con
  // la categoría "Cocción", que es la que hace aparecer este panel.
  const toggleSubcategoria = (cat) => {
    setForm(prev => ({
      ...prev,
      categorias: prev.categorias.includes(cat)
        ? prev.categorias.filter(c => c !== cat)
        : [...prev.categorias, cat]
    }));
  };

  // El panel de subcategorías solo se muestra si la categoría principal a la
  // que pertenecen está seleccionada (o si el producto ya traía alguna
  // subcategoría de antes, para no ocultarla).
  const categoriaConSubcategorias =
    Object.keys(SUBCATEGORIAS).find(cat => form.categorias.includes(cat))
    || Object.keys(SUBCATEGORIAS).find(cat => SUBCATEGORIAS[cat].some(sub => form.categorias.includes(sub)));

  const guardar = async () => {
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k === 'categorias') return;
        data.append(k, v);
      });
      data.append('categorias', JSON.stringify(form.categorias || []));
      if (imagen) data.append('imagen', imagen);
      imagenesNuevas.forEach(f => data.append('imagenesNuevas', f));
      if (video) data.append('video', video);
      if (eliminarImagenes.length) data.append('eliminarImagenes', JSON.stringify(eliminarImagenes));
      if (eliminarVideo) data.append('eliminarVideo', 'true');
      const cfg = { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } };
      if (editandoId) {
        await axios.put(`${API}/productos/${editandoId}`, data, cfg);
      } else {
        await axios.post(`${API}/productos`, data, cfg);
      }
      cancelar();
      cargar();
    } catch(err) {
      alert('Error: ' + (err.response?.data?.error || err.message));
    }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Seguro que quieres eliminar este producto?')) return;
    try {
      await axios.delete(`${API}/productos/${id}`, { headers });
      cargar();
    } catch(err) {
      alert('Error: ' + (err.response?.data?.error || err.message));
    }
  };

  return (
    <div style={s.page}>
      <div style={s.header} className="rf-header">
        <button style={s.btnVolver} onClick={() => window.location.href='/'}>← Volver</button>
        <h1 style={s.titulo}>Productos y Servicios</h1>
        <button style={s.btnNuevo} onClick={abrirNuevo}>+ Nuevo</button>
      </div>

      {mostrarForm && (
        <div style={s.formCard}>
          <h3 style={s.cardTitulo}>{editandoId ? 'Editar Producto' : 'Nuevo Producto / Servicio'}</h3>
          <div style={s.grid3} className="rf-grid3">
            <div><label style={s.label}>Código (opcional)</label><input style={s.input} value={form.codigo} onChange={e => setForm({...form, codigo: e.target.value})} placeholder="Se genera solo si lo dejas vacío" /></div>
            <div><label style={s.label}>Nombre</label><input style={s.input} value={form.nombre} onChange={e => setForm({...form, nombre: e.target.value})} /></div>
            <div>
              <label style={s.label}>Tipo</label>
              <select style={s.input} value={form.tipo} onChange={e => setForm({...form, tipo: e.target.value})}>
                <option value="producto">Producto</option>
                <option value="servicio">Servicio</option>
              </select>
            </div>
            <div><label style={s.label}>Precio base</label><input style={s.input} type="number" value={form.precio_base} onChange={e => setForm({...form, precio_base: e.target.value})} /></div>
            <div>
              <label style={s.label}>Unidad de medida</label>
              <select style={s.input} value={form.unidad} onChange={e => {
                const nuevaUnidad = e.target.value;
                const nuevoStock = nuevaUnidad === 'unidad' ? Math.round(parseFloat(form.stock) || 0) : form.stock;
                setForm({...form, unidad: nuevaUnidad, stock: nuevoStock});
              }}>
                {UNIDADES.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <label style={s.label}>¿Aplica IVA?</label>
              <select style={s.input} value={form.aplica_iva} onChange={e => setForm({...form, aplica_iva: e.target.value === 'true'})}>
                <option value="true">Sí</option>
                <option value="false">No</option>
              </select>
            </div>
            <div>
              <label style={s.label}>Stock</label>
              <input style={s.input} type="number" min="0"
                step={form.unidad === 'unidad' ? '1' : '0.01'}
                value={form.stock || 0} onChange={e => {
                  const valor = form.unidad === 'unidad' ? Math.round(parseFloat(e.target.value) || 0) : e.target.value;
                  setForm({...form, stock: valor});
                }} />
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:'8px', marginTop:'22px' }}>
              <input type="checkbox" id="visible_tienda" checked={form.visible_tienda} onChange={e => setForm({...form, visible_tienda: e.target.checked})} />
              <label htmlFor="visible_tienda" style={{ fontSize:'13px', color:'#333' }}>Mostrar en la tienda</label>
            </div>
            <div style={{gridColumn:'1/-1'}}>
              <label style={s.label}>Categoría</label>
              <div style={s.categoriasWrap}>
                {CATEGORIAS.map(cat => (
                  <label key={cat} style={{ ...s.categoriaChip, ...(form.categorias.includes(cat) ? s.categoriaChipActiva : {}) }}>
                    <input type="radio" name="categoriaUnica" style={{ display:'none' }} checked={form.categorias.includes(cat)} onChange={() => seleccionarCategoria(cat)} />
                    {cat}
                  </label>
                ))}
              </div>
              {categoriaConSubcategorias && (
                <>
                  <p style={s.subcatAyuda}>Subcategorías de {categoriaConSubcategorias} — puedes elegir varias:</p>
                  <div style={{...s.categoriasWrap, marginLeft:'1rem', borderLeft:'2px solid #eee', paddingLeft:'0.8rem'}}>
                    {SUBCATEGORIAS[categoriaConSubcategorias].map(cat => (
                      <label key={cat} style={{ ...s.categoriaChip, ...(form.categorias.includes(cat) ? s.categoriaChipActiva : {}) }}>
                        <input type="checkbox" style={{ display:'none' }} checked={form.categorias.includes(cat)} onChange={() => toggleSubcategoria(cat)} />
                        {cat}
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div style={{gridColumn:'1/-1'}}>
              <label style={s.label}>Descripción</label>
              <textarea
                ref={descripcionRef}
                style={{...s.input, resize:'none', overflow:'hidden', fontFamily:'inherit', display:'block'}}
                rows={1}
                value={form.descripcion}
                onChange={e => setForm({...form, descripcion: e.target.value})}
                onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
              />
            </div>
            <div style={{gridColumn:'1/-1'}}>
              <label style={s.label}>Imagen principal del producto</label>
              <input type="file" accept="image/*" onChange={handleImagen} />
              {preview && <img src={preview} alt="preview" style={s.preview} />}
            </div>

            <div style={{gridColumn:'1/-1', borderTop:'1px solid #eee', paddingTop:'0.8rem', marginTop:'0.4rem'}}>
              <label style={s.label}>Fotos adicionales (para la galería de la tienda, puedes elegir varias)</label>
              <input type="file" accept="image/*" multiple onChange={handleImagenesNuevas} />
              {imagenesExistentes.length > 0 && (
                <div style={{ display:'flex', flexWrap:'wrap', gap:'0.6rem', marginTop:'0.6rem' }}>
                  {imagenesExistentes.map(url => (
                    <div key={url} style={{ textAlign:'center' }}>
                      <img src={imgSrc(url)} alt="" style={{ width:'64px', height:'64px', objectFit:'cover', borderRadius:'6px', border:'1px solid #ddd', opacity: eliminarImagenes.includes(url) ? 0.35 : 1 }} />
                      <label style={{ display:'flex', alignItems:'center', gap:'4px', fontSize:'11px', color:'#888' }}>
                        <input type="checkbox" checked={eliminarImagenes.includes(url)} onChange={() => toggleEliminarImagen(url)} /> Eliminar
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{gridColumn:'1/-1'}}>
              <label style={s.label}>Video promocional (opcional, para la tienda)</label>
              <input type="file" accept="video/*" onChange={handleVideo} />
              {videoExistente && !eliminarVideo && (
                <div style={{ marginTop:'0.6rem' }}>
                  <video src={imgSrc(videoExistente)} controls style={{ width:'160px', borderRadius:'6px', display:'block', marginBottom:'0.3rem' }} />
                  <label style={{ display:'flex', alignItems:'center', gap:'4px', fontSize:'11px', color:'#888' }}>
                    <input type="checkbox" checked={eliminarVideo} onChange={() => setEliminarVideo(!eliminarVideo)} /> Eliminar video actual
                  </label>
                </div>
              )}
            </div>
          </div>
          <div style={s.botonesForm}>
            <button style={s.btnCancelar} onClick={cancelar}>Cancelar</button>
            <button style={s.btnGuardar} onClick={guardar}>{editandoId ? 'Actualizar' : 'Guardar'}</button>
          </div>
        </div>
      )}

      <div style={s.container}>
        <input
          type="text"
          style={s.busqueda}
          placeholder="🔍 Buscar por nombre, código, categoría..."
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
        />
        {productosFiltrados.length === 0 && (
          <p style={{ textAlign:'center', color:'#aaa', padding:'2rem' }}>No se encontraron productos</p>
        )}
        <div style={s.grid4}>
          {productosFiltrados.map(p => (
            <div key={p.id} style={s.productoCard}>
              {p.imagen_url
                ? <img src={imgSrc(p.imagen_url)} alt={p.nombre} style={s.productoImg} />
                : <div style={s.sinImagen}>Sin imagen</div>
              }
              <div style={s.productoInfo}>
                <p style={s.productoCodigo}>{p.codigo}</p>
                <p style={s.productoNombre}>{p.nombre}</p>
                <p style={s.productoTipo}>{p.tipo} · {p.unidad}</p>
                <p style={s.productoPrecio}>$ {Number(p.precio_base).toLocaleString('es-CO')}</p>
                <p style={s.productoIva}>{p.aplica_iva ? 'IVA incluido' : 'Sin IVA'}</p>
                {p.categorias && p.categorias.length > 0 && (
                  <div style={{ display:'flex', flexWrap:'wrap', gap:'4px', marginTop:'4px' }}>
                    {p.categorias.map(cat => (
                      <span key={cat} style={s.categoriaTag}>{cat}</span>
                    ))}
                  </div>
                )}
                {p.visible_tienda === false && (
                  <p style={{ fontSize:'11px', color:'#c00', fontWeight:'700', marginTop:'2px' }}>No visible en tienda</p>
                )}
                <button style={s.btnEditar} onClick={() => abrirEditar(p)}>Editar</button>
                <button style={s.btnEliminar} onClick={() => eliminar(p.id)}>Eliminar</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const s = {
  page:          { minHeight:'100vh', background:'#f5f5f5' },
  header:        { background:'#1a1a2e', padding:'1rem 2rem', display:'flex', justifyContent:'space-between', alignItems:'center' },
  titulo:        { color:'#c8a951', fontSize:'20px', fontWeight:'700' },
  btnVolver:     { background:'transparent', color:'#aaa', border:'1px solid #aaa', padding:'8px 14px', borderRadius:'8px' },
  btnNuevo:      { background:'#c8a951', color:'#fff', border:'none', padding:'8px 16px', borderRadius:'8px', fontWeight:'600' },
  formCard:      { background:'#fff', margin:'1.5rem', padding:'1.5rem', borderRadius:'10px', boxShadow:'0 2px 8px rgba(0,0,0,0.07)' },
  cardTitulo:    { fontSize:'16px', fontWeight:'700', color:'#1a1a2e', marginBottom:'1rem', borderBottom:'2px solid #c8a951', paddingBottom:'6px' },
  grid3:         { display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'1rem', marginBottom:'1rem' },
  label:         { display:'block', fontSize:'13px', color:'#666', marginBottom:'4px' },
  input:         { width:'100%', padding:'9px 12px', border:'1px solid #ddd', borderRadius:'6px', fontSize:'14px' },
  botonesForm:   { display:'flex', gap:'12px', justifyContent:'flex-end' },
  btnCancelar:   { background:'transparent', color:'#888', border:'1px solid #ddd', padding:'10px 24px', borderRadius:'8px' },
  btnGuardar:    { background:'#1a1a2e', color:'#fff', border:'none', padding:'10px 24px', borderRadius:'8px', fontWeight:'600' },
  btnEditar:     { marginTop:'8px', width:'100%', background:'#1a1a2e', color:'#fff', border:'none', padding:'6px', borderRadius:'6px', fontSize:'13px', cursor:'pointer' },
  btnEliminar:   { marginTop:'4px', width:'100%', background:'#fee', color:'#c00', border:'1px solid #fcc', padding:'6px', borderRadius:'6px', fontSize:'13px', cursor:'pointer' },
  preview:       { width:'120px', height:'120px', objectFit:'cover', borderRadius:'8px', marginTop:'8px', border:'1px solid #ddd' },
  container:     { padding:'1.5rem' },
  busqueda:      { width:'100%', maxWidth:'420px', padding:'10px 14px', border:'1px solid #ddd', borderRadius:'8px', fontSize:'14px', marginBottom:'1rem', display:'block', background:'#fff' },
  grid4:         { display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:'1.5rem' },
  productoCard:  { background:'#fff', borderRadius:'10px', overflow:'hidden', boxShadow:'0 2px 8px rgba(0,0,0,0.08)' },
  productoImg:   { width:'100%', height:'160px', objectFit:'cover' },
  sinImagen:     { width:'100%', height:'160px', background:'#f0f0f0', display:'flex', alignItems:'center', justifyContent:'center', color:'#aaa', fontSize:'13px' },
  productoInfo:  { padding:'1rem' },
  productoCodigo:{ fontSize:'11px', color:'#aaa', marginBottom:'2px' },
  productoNombre:{ fontWeight:'700', fontSize:'15px', color:'#1a1a2e', marginBottom:'4px' },
  productoTipo:  { fontSize:'12px', color:'#888', marginBottom:'4px' },
  productoPrecio:{ fontSize:'16px', fontWeight:'700', color:'#c8a951' },
  productoIva:   { fontSize:'11px', color:'#888', marginTop:'2px' },
  categoriasWrap:{ display:'flex', flexWrap:'wrap', gap:'8px', marginTop:'4px' },
  categoriaChip: { display:'inline-block', padding:'6px 12px', borderRadius:'20px', border:'1px solid #ddd', fontSize:'12px', color:'#555', cursor:'pointer', userSelect:'none', background:'#fafafa' },
  categoriaChipActiva: { background:'#c8a951', borderColor:'#c8a951', color:'#fff', fontWeight:'600' },
  subcatAyuda:   { fontSize:'11px', color:'#999', margin:'0.6rem 0 0.3rem' },
  categoriaTag:  { display:'inline-block', padding:'2px 8px', borderRadius:'10px', background:'#f0ede4', color:'#8a6d1f', fontSize:'10px', fontWeight:'600' },
};